import { devDebug } from "../lib/devLog";
import { useCallback, useEffect, useState } from "react";
import { signedGet, signedPatch } from "../lib/signedRequest";
import { loadIdentity } from "../lib/identity";
import { playDmPing } from "../lib/dmSoundPref";

// Fetches the aggregated activity feed (follows / replies / reactions) for
// the current viewer, along with a `recentCount` (unread items since the
// last "mark all read" event, or last 24h if never opened). Polled every
// 30 seconds. Also listens to the `ol:notifications-refresh` window event so
// two instances of this hook (e.g. header bell + notifications page) stay
// in sync after a mark-all-read action.

const REFRESH_EVENT = "ol:notifications-refresh";
let knownNotificationIds = null;
let lastNotificationSoundAt = 0;

function detectNewNotifications(items) {
  const ids = new Set(items.map((item) => item.id).filter(Boolean));
  if (knownNotificationIds === null) {
    knownNotificationIds = ids;
    return;
  }
  const hasNew = items.some((item) => item.id && !knownNotificationIds.has(item.id));
  knownNotificationIds = ids;
  if (!hasNew || Date.now() - lastNotificationSoundAt < 1000) return;
  lastNotificationSoundAt = Date.now();
  playDmPing();
}

export function useNotifications(pollMs = 30000) {
  const [items, setItems] = useState([]);
  const [recentCount, setRecentCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    const id = loadIdentity();
    if (!id) {
      setItems([]);
      setRecentCount(0);
      setLoading(false);
      return;
    }
    try {
      const r = await signedGet("/api/v1/notifications");
      const nextItems = r.data?.items || [];
      detectNewNotifications(nextItems);
      setItems(nextItems);
      setRecentCount(r.data?.recentCount || 0);
      setError(null);
    } catch (err) {
      devDebug("useNotifications: fetch failed", err);
      setError(err?.message || "Errore");
    } finally {
      setLoading(false);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    // Optimistic update: hide the badge and flip local items to read before
    // the server confirms.
    setRecentCount(0);
    setItems((prev) => prev.map((n) => (n.read ? n : { ...n, read: true })));
    try {
      await signedPatch("/api/v1/notifications/read-all", {});
      // Broadcast so other mounted instances (header bell) refresh too.
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(REFRESH_EVENT));
      }
    } catch (err) {
      devDebug("useNotifications: markAllRead failed", err);
      // Revert to server-truth on failure.
      refresh();
      throw err;
    }
  }, [refresh]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, pollMs);
    const onEvent = () => refresh();
    if (typeof window !== "undefined") {
      window.addEventListener(REFRESH_EVENT, onEvent);
    }
    return () => {
      clearInterval(t);
      if (typeof window !== "undefined") {
        window.removeEventListener(REFRESH_EVENT, onEvent);
      }
    };
  }, [refresh, pollMs]);

  return { items, recentCount, loading, error, refresh, markAllRead };
}
